import { motion } from 'motion/react';
import { Link, useNavigate } from '@tanstack/react-router';
import { formatRelativeTime } from '@/lib/utils';
import { useUpdateQuizlet, useDeleteQuizlet } from '@/queries/quizlets';
import SquarePenIcon from '@/assets/common/square-pen.svg?react';
import styles from './QuizletCard.module.scss';
import type { Quizlet } from '@/types';

interface QuizletCardProps {
  quizlet: Quizlet;
  index?: number;
}

export function QuizletCard({ quizlet, index = 0 }: QuizletCardProps) {
  const navigate = useNavigate();
  const updateQuizlet = useUpdateQuizlet();
  const deleteQuizlet = useDeleteQuizlet();

  const handleToggleDailyKnowledge = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    updateQuizlet.mutate({
      id: quizlet.id,
      data: { is_daily_knowledge: !quizlet.is_daily_knowledge },
    });
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate({
      to: '/quizlets/$quizletId/edit',
      params: { quizletId: quizlet.id },
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this quizlet?')) {
      deleteQuizlet.mutate(quizlet.id);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.3,
        delay: index * 0.05,
        type: 'spring',
        stiffness: 100,
      }}
      style={{ height: '100%', minWidth: 0 }}
    >
      <Link
        to="/quizlets/$quizletId"
        params={{ quizletId: quizlet.id }}
        className={styles.quizletCard}
      >
        <div className={styles.quizletCard__header}>
          <h3 className={styles.quizletCard__title}>{quizlet.name}</h3>
          <button
            onClick={handleToggleDailyKnowledge}
            className={styles.quizletCard__toggle}
            title={quizlet.is_daily_knowledge ? 'Remove from daily knowledge' : 'Add to daily knowledge'}
            aria-label={quizlet.is_daily_knowledge ? 'Remove from daily knowledge' : 'Add to daily knowledge'}
          >
            <motion.span
              className={`${styles.quizletCard__toggleIcon} ${
                quizlet.is_daily_knowledge ? styles['quizletCard__toggleIcon--active'] : ''
              }`}
            >
              {quizlet.is_daily_knowledge ? '★' : '☆'}
            </motion.span>
          </button>
        </div>

        <p className={styles.quizletCard__prompt}>{quizlet.original_prompt}</p>

        <div className={styles.quizletCard__stats}>
          <div className={styles.quizletCard__badge}>
            <SquarePenIcon className={styles.quizletCard__badgeIcon} />
            {quizlet.questions?.length || 0} Questions
          </div>
        </div>

        <div className={styles.quizletCard__footer}>
          <div className={styles.quizletCard__meta}>
            <span className={styles.quizletCard__metaItem}>
              Created {formatRelativeTime(quizlet.created_at)}
            </span>
            {quizlet.last_practiced_at && (
              <span className={styles.quizletCard__metaItem}>
                Practiced {formatRelativeTime(quizlet.last_practiced_at)}
              </span>
            )}
          </div>

          <div className={styles.quizletCard__actions}>
            <button
              onClick={handleEdit}
              className={styles.quizletCard__actionButton}
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              className={`${styles.quizletCard__actionButton} ${styles['quizletCard__actionButton--danger']}`}
            >
              Delete
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
